import type { CollectionItem, CollectionRow } from '@panelvault/shared';
import { CheckSquare, LayoutGrid, Library, Table2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { CoverCard } from '../components/CoverCard';
import { EmptyState } from '../components/EmptyState';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select } from '../components/ui/controls';
import { Input } from '../components/ui/input';
import { Panel } from '../components/ui/panel';
import { CoverSkeleton } from '../components/ui/skeleton';
import { BulkEditDialog } from '../features/collection/BulkEditDialog';
import { CollectionItemDialog } from '../features/collection/CollectionItemDialog';
import { useCollection, useFacets, type CollectionParams } from '../features/collection/queries';
import { imageUrl } from '../lib/api';
import { cn } from '../lib/utils';

type ViewMode = 'grid' | 'table';

const VIEW_STORAGE_KEY = 'pv-collection-view';

function readView(): ViewMode {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === 'table' ? 'table' : 'grid';
  } catch {
    return 'grid';
  }
}

export function CollectionPage() {
  const { t } = useTranslation();
  const facets = useFacets();

  const [params, setParams] = useState<CollectionParams>({ page: 1, perPage: 48 });
  const [view, setView] = useState<ViewMode>(readView);
  const [selection, setSelection] = useState<string[]>([]);
  const [editing, setEditing] = useState<CollectionRow | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const collection = useCollection(params);
  const locations = facets.data?.locations.map((entry) => entry.value) ?? [];

  function update(patch: CollectionParams) {
    setParams((current) => ({ ...current, ...patch, page: patch.page ?? 1 }));
  }

  function switchView(next: ViewMode) {
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Remembering the view is a convenience, not a requirement.
    }
  }

  function toggleSelected(id: string) {
    setSelection((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );
  }

  const rows = collection.data?.items ?? [];
  const total = collection.data?.total ?? 0;
  const page = params.page ?? 1;
  const perPage = params.perPage ?? 48;
  const pageCount = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl text-foreground">{t('nav.collection')}</h1>
          <p className="mt-1 text-sm text-muted">{t('collection.count', { count: total })}</p>
        </div>

        <div className="flex items-center gap-1 rounded-lg border bg-surface p-1">
          <Button
            variant={view === 'grid' ? 'surface' : 'ghost'}
            size="sm"
            onClick={() => switchView('grid')}
            aria-pressed={view === 'grid'}
          >
            <LayoutGrid className="size-4" />
            {t('collection.grid')}
          </Button>
          <Button
            variant={view === 'table' ? 'surface' : 'ghost'}
            size="sm"
            onClick={() => switchView('table')}
            aria-pressed={view === 'table'}
          >
            <Table2 className="size-4" />
            {t('collection.table')}
          </Button>
        </div>
      </header>

      <Panel className="flex flex-col gap-3 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            placeholder={t('collection.search')}
            value={params.q ?? ''}
            onChange={(event) => update({ q: event.target.value })}
          />

          <Select
            value={params.publisherId ?? ''}
            onChange={(event) => update({ publisherId: event.target.value })}
            aria-label={t('series.publisher')}
          >
            <option value="">{t('filter.allPublishers')}</option>
            {facets.data?.publishers.map((publisher) => (
              <option key={publisher.id} value={publisher.id}>
                {publisher.name} ({publisher.count})
              </option>
            ))}
          </Select>

          <Select
            value={params.location ?? ''}
            onChange={(event) => update({ location: event.target.value })}
            aria-label={t('collection.location')}
          >
            <option value="">{t('filter.allLocations')}</option>
            {facets.data?.locations.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.value} ({entry.count})
              </option>
            ))}
          </Select>

          <Select
            value={params.isRead ?? ''}
            onChange={(event) =>
              update({ isRead: (event.target.value || undefined) as 'true' | 'false' | undefined })
            }
            aria-label={t('collection.isRead')}
          >
            <option value="">{t('filter.allRead')}</option>
            <option value="true">{t('filter.read')}</option>
            <option value="false">{t('filter.unread')}</option>
          </Select>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Select
            className="h-9 w-auto"
            value={params.sort ?? 'addedAt'}
            onChange={(event) => update({ sort: event.target.value as CollectionParams['sort'] })}
            aria-label={t('filter.sort')}
          >
            <option value="addedAt">{t('filter.sortAdded')}</option>
            <option value="series">{t('filter.sortSeries')}</option>
            <option value="number">{t('filter.sortNumber')}</option>
            <option value="releaseDate">{t('filter.sortRelease')}</option>
          </Select>

          <Select
            className="h-9 w-auto"
            value={params.order ?? 'desc'}
            onChange={(event) => update({ order: event.target.value as 'asc' | 'desc' })}
            aria-label={t('filter.order')}
          >
            <option value="desc">{t('filter.desc')}</option>
            <option value="asc">{t('filter.asc')}</option>
          </Select>

          {selection.length > 0 ? (
            <div className="ml-auto flex items-center gap-2">
              <Badge variant="accent">{t('bulk.selected', { count: selection.length })}</Badge>
              <Button size="sm" onClick={() => setBulkOpen(true)}>
                <CheckSquare className="size-4" />
                {t('bulk.edit')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelection([])}>
                <X className="size-4" />
              </Button>
            </div>
          ) : null}
        </div>
      </Panel>

      {collection.isPending ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 12 }, (_, index) => (
            <CoverSkeleton key={index} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Library}
          title={t('collection.emptyTitle')}
          description={t('collection.emptyBody')}
          action={
            <Link
              to="/series"
              className="inline-flex h-9 items-center rounded-lg bg-accent px-3 text-sm font-medium text-accent-foreground hover:bg-accent-strong"
            >
              {t('collection.emptyAction')}
            </Link>
          }
        />
      ) : view === 'grid' ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {rows.map((row) => (
            <div key={row.item.id} className="relative">
              <input
                type="checkbox"
                checked={selection.includes(row.item.id)}
                onChange={() => toggleSelected(row.item.id)}
                aria-label={t('bulk.select')}
                className="absolute top-2 right-2 z-10 size-5 rounded border bg-background/80 accent-[var(--pv-accent)]"
              />
              <CoverCard
                title={row.seriesTitle}
                number={row.issue.number}
                subtitle={[row.publisherName, row.item.location].filter(Boolean).join(' · ')}
                imageUrl={imageUrl(row.coverImageId, 'medium')}
                quantity={row.item.quantity}
                onClick={() => setEditing(row)}
              />
            </div>
          ))}
        </div>
      ) : (
        <Panel className="overflow-x-auto">
          <table className="w-full min-w-[48rem] text-sm">
            <thead className="border-b text-left text-xs tracking-wide text-muted uppercase">
              <tr>
                <th className="w-10 px-3 py-2" />
                <th className="px-3 py-2">{t('series.title')}</th>
                <th className="px-3 py-2">{t('issue.number')}</th>
                <th className="px-3 py-2">{t('series.publisher')}</th>
                <th className="px-3 py-2">{t('collection.location')}</th>
                <th className="px-3 py-2">{t('collection.quantity')}</th>
                <th className="px-3 py-2">{t('collection.isRead')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.item.id}
                  className={cn(
                    'cursor-pointer border-b last:border-0 hover:bg-surface-raised',
                    selection.includes(row.item.id) && 'bg-surface-raised',
                  )}
                  onClick={() => setEditing(row)}
                >
                  <td className="px-3 py-2" onClick={(event) => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selection.includes(row.item.id)}
                      onChange={() => toggleSelected(row.item.id)}
                      aria-label={t('bulk.select')}
                      className="size-4 accent-[var(--pv-accent)]"
                    />
                  </td>
                  <td className="px-3 py-2 text-foreground">
                    <Link
                      to={`/series/${row.seriesId}`}
                      className="hover:text-accent"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {row.seriesTitle}
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-display text-foreground">{row.issue.number}</td>
                  <td className="px-3 py-2 text-muted">{row.publisherName ?? '–'}</td>
                  <td className="px-3 py-2 text-muted">{row.item.location ?? '–'}</td>
                  <td className="px-3 py-2 text-muted">{row.item.quantity}</td>
                  <td className="px-3 py-2">
                    {row.item.isRead ? (
                      <Badge variant="success">{t('filter.read')}</Badge>
                    ) : (
                      <Badge variant="outline">{t('filter.unread')}</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {pageCount > 1 ? (
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="surface"
            size="sm"
            disabled={page <= 1}
            onClick={() => update({ page: page - 1 })}
          >
            {t('pagination.previous')}
          </Button>
          <span className="text-sm text-muted">{t('pagination.page', { page, pageCount })}</span>
          <Button
            variant="surface"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => update({ page: page + 1 })}
          >
            {t('pagination.next')}
          </Button>
        </div>
      ) : null}

      <CollectionItemDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        item={(editing?.item as CollectionItem | undefined) ?? null}
        title={editing ? `${editing.seriesTitle} #${editing.issue.number}` : ''}
        knownLocations={locations}
      />

      <BulkEditDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        ids={selection}
        knownLocations={locations}
        onDone={() => setSelection([])}
      />
    </div>
  );
}
